import { ModelComparisonSelection } from "../modelComparisonTypes";

import AssertionPresetSetup from "./AssertionPresetSetup";
import BrandVisibilityPresetSetup from "./BrandVisibilityPresetSetup";
import { BrandVisibilityPresetSubmitPayload } from "./brandVisibilityTypes";
import CustomPresetSetup from "./CustomPresetSetup";
import GenerateWithAIPresetSetup from "./GenerateWithAIPresetSetup";
import ModelComparisonPresetSetup from "./ModelComparisonPresetSetup";

import { EvaluationPresetType } from "@/queries/smart-tables";
import { PromptVersion } from "@/types/apiGetters";

export type EvaluationPresetStep =
  | "model-comparison"
  | "brand-visibility"
  | "generate-with-ai"
  | "custom"
  | EvaluationPresetType;

interface EvaluationPresetProps {
  step: EvaluationPresetStep | null;
  promptVersion?: PromptVersion;
  isSubmittingModelComparison: boolean;
  isSubmittingCustom: boolean;
  isSubmittingEvaluation: boolean;
  isSubmittingBrandVisibility?: boolean;
  isSubmittingGenerateWithAI?: boolean;
  onSubmitModelComparison: (
    selections: ModelComparisonSelection[],
    initialInputs: Record<string, string>,
  ) => void;
  onSubmitCustom: (initialInputs: Record<string, string>) => void;
  onSubmitBrandVisibility?: (payload: BrandVisibilityPresetSubmitPayload) => void;
  onSubmitGenerateWithAI?: (message: string) => void;
  onSubmitEvaluation: (payload: {
    presetType: EvaluationPresetType;
    initialInputs: Record<string, string>;
    expectedValue?: string;
    assertions: string[];
  }) => void;
}

const EvaluationPreset = ({
  step,
  promptVersion,
  isSubmittingModelComparison,
  isSubmittingCustom,
  isSubmittingEvaluation,
  onSubmitModelComparison,
  onSubmitCustom,
  onSubmitBrandVisibility,
  onSubmitGenerateWithAI,
  onSubmitEvaluation,
  isSubmittingBrandVisibility = false,
  isSubmittingGenerateWithAI = false,
}: EvaluationPresetProps) => {
  if (!step) {
    return null;
  }

  if (step === "model-comparison") {
    return (
      <ModelComparisonPresetSetup
        promptVersion={promptVersion}
        isSubmitting={isSubmittingModelComparison}
        onSubmit={onSubmitModelComparison}
      />
    );
  }

  if (step === "custom") {
    return (
      <CustomPresetSetup
        promptVersion={promptVersion}
        isSubmitting={isSubmittingCustom}
        onSubmit={onSubmitCustom}
      />
    );
  }

  if (step === "brand-visibility") {
    return (
      <BrandVisibilityPresetSetup
        isSubmitting={isSubmittingBrandVisibility}
        onSubmit={payload => onSubmitBrandVisibility?.(payload)}
      />
    );
  }

  if (step === "generate-with-ai") {
    return (
      <GenerateWithAIPresetSetup
        isSubmitting={isSubmittingGenerateWithAI}
        onSubmit={message => onSubmitGenerateWithAI?.(message)}
      />
    );
  }

  return (
    <AssertionPresetSetup
      presetType={step}
      promptVersion={promptVersion}
      isSubmitting={isSubmittingEvaluation}
      onSubmit={onSubmitEvaluation}
    />
  );
};

export default EvaluationPreset;
